import React, { useState, useEffect, useRef, useCallback } from "react";
import AdminLayout from "../../components/admin/AdminLayout.jsx";
import { API_BASE, apiFetch } from "../../utils/api";
import "./AdminTestimonials.css";

/* webpack require.context for testimonial photos */
let TESTI_PHOTOS = {};
try {
  const ctx = require.context(
    "../../assets/uploads/testimonials",
    false,
    /\.(png|jpe?g|gif|webp|svg)$/i
  );
  TESTI_PHOTOS = ctx.keys().reduce((map, key) => {
    map[key.replace(/^\.\//, "")] = ctx(key);
    return map;
  }, {});
} catch (_) {
  /* outside CRA — fall back to API URL */
}

function testiPhoto(filename) {
  if (!filename) return null;
  return TESTI_PHOTOS[filename] ?? `${API_BASE}/uploads/testimonials/${filename}`;
}

const EMPTY_FORM = {
  name:          "",
  position:      "",
  company:       "",
  content:       "",
  rating:        5,
  show_on_pages: "",
  order:         0,
  active:        1,
  photo_filename: null,  // new File upload
};

/* ─────────────────────────────────
   Star Rating Display
   ───────────────────────────────── */
function StarDisplay({ rating, size = "atest-star" }) {
  return (
    <div className="atest-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={`${size} ${n <= rating ? "atest-star--filled" : ""}`}>
          ★
        </span>
      ))}
    </div>
  );
}

/* ─────────────────────────────────
   Star Rating Picker
   ───────────────────────────────── */
function StarPicker({ value, onChange }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="atest-star-picker">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          className={`atest-star-btn ${n <= (hovered || value) ? "atest-star-btn--filled" : ""}`}
          onMouseEnter={() => setHovered(n)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onChange(n)}
          title={`${n} star${n !== 1 ? "s" : ""}`}
        >
          ★
        </button>
      ))}
      <span style={{ fontSize: ".78rem", color: "#9ca3af", marginLeft: 6 }}>
        {hovered || value} / 5
      </span>
    </div>
  );
}

/* ─────────────────────────────────
   Status Pill
   ───────────────────────────────── */
function StatusPill({ active }) {
  return (
    <span className={`atest-status-pill ${Number(active) === 1 ? "atest-status-pill--active" : "atest-status-pill--inactive"}`}>
      {Number(active) === 1 ? "● Active" : "○ Inactive"}
    </span>
  );
}

/* ─────────────────────────────────
   Page Tags
   ───────────────────────────────── */
function PageTags({ showOnPages }) {
  if (!showOnPages) return null;
  const pages = showOnPages.split(",").map((p) => p.trim()).filter(Boolean);
  if (!pages.length) return null;
  return (
    <div className="atest-card__pages">
      {pages.map((p) => (
        <span key={p} className="atest-page-tag">{p}</span>
      ))}
    </div>
  );
}

/* ─────────────────────────────────
   Testimonial Card
   ───────────────────────────────── */
function TestiCard({ item, onEdit, onDelete, onToggle }) {
  const photoSrc = testiPhoto(item.photo_filename);
  const metaParts = [item.position, item.company].filter(Boolean).join(" · ");

  return (
    <div className={`atest-card ${Number(item.active) !== 1 ? "atest-card--inactive" : ""}`}>
      <div className="atest-card__head">
        {photoSrc ? (
          <img src={photoSrc} alt={item.name} className="atest-card__avatar" />
        ) : (
          <div className="atest-card__avatar-placeholder">🧑</div>
        )}
        <div className="atest-card__nameblock">
          <p className="atest-card__name">{item.name}</p>
          {metaParts && (
            <p className="atest-card__meta">
              <strong>{item.position}</strong>
              {item.position && item.company ? " · " : ""}
              {item.company}
            </p>
          )}
          <StarDisplay rating={Number(item.rating) || 5} />
        </div>
        <div style={{ marginLeft: "auto", flexShrink: 0 }}>
          <StatusPill active={item.active} />
        </div>
      </div>

      <div className="atest-card__body">
        <p className="atest-card__quote">{item.content}</p>
      </div>

      <PageTags showOnPages={item.show_on_pages} />

      <div className="atest-card__footer">
        <div className="atest-card__footer-left">
          <span className="atest-card__order"># {item.order ?? 0}</span>
        </div>
        <div className="atest-card__actions">
          <button
            className={`atest-icon-btn ${Number(item.active) === 1 ? "atest-icon-btn--toggle-on" : "atest-icon-btn--toggle-off"}`}
            title={Number(item.active) === 1 ? "Deactivate" : "Activate"}
            onClick={() => onToggle(item)}
          >
            {Number(item.active) === 1 ? "✓" : "○"}
          </button>
          <button
            className="atest-icon-btn atest-icon-btn--edit"
            title="Edit"
            onClick={() => onEdit(item)}
          >
            ✎
          </button>
          <button
            className="atest-icon-btn atest-icon-btn--delete"
            title="Delete"
            onClick={() => onDelete(item)}
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────
   Modal (Add / Edit)
   ───────────────────────────────── */
function TestiModal({ item, onClose, onSaved }) {
  const isEdit = !!item?.id;
  const [form, setForm]       = useState(EMPTY_FORM);
  const [preview, setPreview] = useState(null);
  const [clearPhoto, setClearPhoto] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (item) {
      setForm({
        name:           item.name           || "",
        position:       item.position       || "",
        company:        item.company        || "",
        content:        item.content        || "",
        rating:         Number(item.rating) || 5,
        show_on_pages:  item.show_on_pages  || "",
        order:          item.order          ?? 0,
        active:         Number(item.active) === 1 ? 1 : 0,
        photo_filename: null,
      });
      setPreview(item.photo_filename ? testiPhoto(item.photo_filename) : null);
      setClearPhoto(false);
    } else {
      setForm(EMPTY_FORM);
      setPreview(null);
      setClearPhoto(false);
    }
  }, [item]);

  const set = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    setError("");
  };

  const handleFile = (file) => {
    if (!file) return;
    set("photo_filename", file);
    setClearPhoto(false);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleClearPhoto = () => {
    set("photo_filename", null);
    setPreview(null);
    setClearPhoto(true);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleSave = async () => {
    setError("");
    if (!form.name.trim())    return setError("Name is required.");
    if (!form.content.trim()) return setError("Testimonial content is required.");

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name",          form.name.trim());
      fd.append("position",      form.position      || "");
      fd.append("company",       form.company       || "");
      fd.append("content",       form.content.trim());
      fd.append("rating",        form.rating);
      fd.append("show_on_pages", form.show_on_pages || "");
      fd.append("order",         form.order);
      fd.append("active",        form.active);
      if (form.photo_filename) fd.append("photo_filename", form.photo_filename);
      if (clearPhoto)          fd.append("clear_photo", "1");

      if (isEdit) {
        await apiFetch(`/api/admin/testimonials/${item.id}`, { method: "PUT", body: fd });
      } else {
        await apiFetch("/api/admin/testimonials", { method: "POST", body: fd });
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="atest-modal-backdrop" onClick={handleBackdrop}>
      <div className="atest-modal">
        <div className="atest-modal__header">
          <h2 className="atest-modal__title">
            {isEdit ? `Edit — ${item.name}` : "Add Solution Step"}
          </h2>
          <button className="atest-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="atest-modal__body">
          {error && <div className="atest-alert atest-alert--error" style={{ marginBottom: 16 }}>⚠ {error}</div>}

          {/* ── Person ── */}
          <div className="atest-msec">
            <h4 className="atest-msec__title">Person</h4>
            <div className="atest-two-col">
              <div className="atest-field">
                <label className="atest-field__label">Full Name <span className="atest-req">*</span></label>
                <input
                  className="atest-input"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="John Doe"
                />
              </div>
              <div className="atest-field">
                <label className="atest-field__label">Position / Title</label>
                <input
                  className="atest-input"
                  value={form.position}
                  onChange={(e) => set("position", e.target.value)}
                  placeholder="Fleet Manager"
                />
              </div>
              <div className="atest-field">
                <label className="atest-field__label">Company</label>
                <input
                  className="atest-input"
                  value={form.company}
                  onChange={(e) => set("company", e.target.value)}
                  placeholder="Acme Corp"
                />
              </div>
              <div className="atest-field">
                <label className="atest-field__label">Star Rating</label>
                <StarPicker value={form.rating} onChange={(v) => set("rating", v)} />
              </div>
            </div>
          </div>

          {/* ── Testimonial ── */}
          <div className="atest-msec">
            <h4 className="atest-msec__title">Testimonial</h4>
            <div className="atest-field">
              <label className="atest-field__label">Content <span className="atest-req">*</span></label>
              <textarea
                className="atest-textarea"
                rows={5}
                value={form.content}
                onChange={(e) => set("content", e.target.value)}
                placeholder="Write the solution text here…"
              />
            </div>
          </div>

          {/* ── Photo ── */}
          <div className="atest-msec">
            <h4 className="atest-msec__title">Photo</h4>
            <div className="atest-photo-wrap">
              <div
                className={`atest-photo-drop ${preview ? "atest-photo-drop--has-photo" : ""}`}
                onClick={() => fileRef.current?.click()}
              >
                {preview ? (
                  <img src={preview} alt="Preview" className="atest-photo-drop__preview" />
                ) : (
                  <div className="atest-photo-drop__placeholder">
                    <span className="atest-photo-drop__icon">🧑</span>
                    <span className="atest-photo-drop__text">Click to upload photo</span>
                    <span className="atest-photo-drop__sub">PNG, JPG, WebP — max 10 MB</span>
                  </div>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => handleFile(e.target.files[0])}
                />
              </div>
              <div className="atest-photo-actions">
                <button type="button" className="atest-image-btn" onClick={() => fileRef.current?.click()}>
                  {preview ? "Change Photo" : "Upload Photo"}
                </button>
                {preview && (
                  <button type="button" className="atest-image-btn atest-image-btn--remove" onClick={handleClearPhoto}>
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* ── Display Settings ── */}
          <div className="atest-msec">
            <h4 className="atest-msec__title">Display Settings</h4>
            <div className="atest-two-col">
              <div className="atest-field">
                <label className="atest-field__label">Show On Pages</label>
                <input
                  className="atest-input"
                  value={form.show_on_pages}
                  onChange={(e) => set("show_on_pages", e.target.value)}
                  placeholder="home, about-us, services"
                />
                <p className="atest-pages-hint">
                  Comma-separated page slugs, e.g.{" "}
                  <code>home</code>, <code>about-us</code>, <code>services</code>
                </p>
              </div>
              <div className="atest-field">
                <label className="atest-field__label">Display Order</label>
                <p className="atest-field__hint">Lower numbers appear first.</p>
                <input
                  className="atest-input"
                  type="number"
                  min="0"
                  value={form.order}
                  onChange={(e) => set("order", parseInt(e.target.value) || 0)}
                />
              </div>
            </div>
            <div className="atest-field">
              <label className="atest-check">
                <input
                  type="checkbox"
                  checked={Number(form.active) === 1}
                  onChange={(e) => set("active", e.target.checked ? 1 : 0)}
                />
                <span>Active (visible on site)</span>
              </label>
            </div>
          </div>
        </div>

        <div className="atest-modal__footer">
          <div />
          <div className="atest-modal__footer-right">
            <button className="atest-btn atest-btn--cancel" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button className="atest-btn atest-btn--save" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Add Solution"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────
   Confirm Delete Dialog
   ───────────────────────────────── */
function ConfirmDialog({ item, onCancel, onConfirm, deleting }) {
  return (
    <div className="atest-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="atest-confirm">
        <div className="atest-confirm__icon">🗑</div>
        <h3 className="atest-confirm__title">Delete Solution?</h3>
        <p className="atest-confirm__msg">
          This will permanently remove the solution from <strong>{item.name}</strong> and delete their photo. This action cannot be undone.
        </p>
        <div className="atest-confirm__actions">
          <button className="atest-btn atest-btn--cancel" onClick={onCancel} disabled={deleting}>
            Cancel
          </button>
          <button className="atest-btn atest-btn--danger" onClick={onConfirm} disabled={deleting}>
            {deleting ? "Deleting..." : "Yes, Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════ */
export default function AdminTestimonials() {
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");
  const [success,  setSuccess]  = useState("");

  /* filters */
  const [search,       setSearch]       = useState("");
  const [filterActive, setFilterActive] = useState("");
  const [filterRating, setFilterRating] = useState("");
  const [sort,         setSort]         = useState("order_asc");

  /* modals */
  const [editTarget,   setEditTarget]   = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting,     setDeleting]     = useState(false);

  /* ── Load ── */
  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ sort, limit: 200 });
    if (search)           params.set("search", search);
    if (filterActive !== "") params.set("active", filterActive);
    if (filterRating !== "") params.set("rating", filterRating);

    apiFetch(`/api/admin/testimonials?${params}`)
      .then((data) => setItems(data.testimonials || []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [search, filterActive, filterRating, sort]);

  useEffect(() => { load(); }, [load]);

  /* ── Stats ── */
  const totalActive   = items.filter((i) => Number(i.active) === 1).length;
  const totalInactive = items.filter((i) => Number(i.active) !== 1).length;
  const avgRating     = items.length
    ? (items.reduce((s, i) => s + (Number(i.rating) || 0), 0) / items.length).toFixed(1)
    : "—";

  /* ── Toggle ── */
  const handleToggle = async (item) => {
    setError(""); setSuccess("");
    try {
      const res = await apiFetch(`/api/admin/testimonials/${item.id}/toggle`, { method: "PUT" });
      setSuccess(res.message || "Status updated.");
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  /* ── Delete ── */
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiFetch(`/api/admin/testimonials/${deleteTarget.id}`, { method: "DELETE" });
      setSuccess(`Testimonial from ${deleteTarget.name} deleted.`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  /* ── After save ── */
  const handleSaved = () => {
    setEditTarget(null);
    setSuccess("Solution saved successfully.");
    load();
  };

  if (loading) {
    return (
      <AdminLayout pageTag="CONTENT" pageTitle="Testimonials">
        <div className="atest-skeleton" />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout pageTag="CONTENT" pageTitle="Testimonials">

      {/* ── Intro bar ── */}
      <div className="atest-intro-bar">
        <div className="atest-intro-bar__left">
          <span className="atest-intro-bar__icon">⭐</span>
          <div>
            <h2 className="atest-intro-bar__title">Solution</h2>
            <p className="atest-intro-bar__sub">
              Manage customer reviews and solutions displayed across your site.
            </p>
          </div>
        </div>
        <div className="atest-intro-bar__right">
          <span className="atest-stat-pill">
            <span className="atest-stat-pill__num">{totalActive}</span> Active
          </span>
          <span className="atest-stat-pill">
            <span className="atest-stat-pill__num">{totalInactive}</span> Inactive
          </span>
          <span className="atest-stat-pill">
            ★ <span className="atest-stat-pill__num">{avgRating}</span> Avg Rating
          </span>
        </div>
      </div>

      {/* ── Alerts ── */}
      {error   && <div className="atest-alert atest-alert--error">⚠ {error}</div>}
      {success && <div className="atest-alert atest-alert--success">✓ {success}</div>}

      {/* ── Toolbar ── */}
      <div className="atest-toolbar">
        <div className="atest-toolbar__left">
          <div className="atest-search-wrap">
            <span className="atest-search-icon">⌕</span>
            <input
              className="atest-search"
              type="text"
              placeholder="Search Solution steps..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="atest-filter-select"
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
          <select
            className="atest-filter-select"
            value={filterRating}
            onChange={(e) => setFilterRating(e.target.value)}
          >
            <option value="">All Ratings</option>
            <option value="5">5 Stars</option>
            <option value="4">4 Stars</option>
            <option value="3">3 Stars</option>
            <option value="2">2 Stars</option>
            <option value="1">1 Star</option>
          </select>
          <select
            className="atest-filter-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
          >
            <option value="order_asc">Order ↑</option>
            <option value="order_desc">Order ↓</option>
            <option value="rating_desc">Rating ↓</option>
            <option value="rating_asc">Rating ↑</option>
            <option value="name_asc">Name A-Z</option>
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
          </select>
        </div>
        <button className="atest-btn-add" onClick={() => setEditTarget({})}>
          + Add Solution Step
        </button>
      </div>

      {/* ── Grid ── */}
      <div className="atest-grid">
        {items.length === 0 ? (
          <div className="atest-empty">
            <div className="atest-empty__icon">⭐</div>
            <h3 className="atest-empty__title">No Solution Step Found</h3>
            <p className="atest-empty__sub">
              {search || filterActive !== "" || filterRating !== ""
                ? "Try adjusting your filters."
                : "Click \"Add Testimonial\" to get started."}
            </p>
          </div>
        ) : (
          items.map((item) => (
            <TestiCard
              key={item.id}
              item={item}
              onEdit={(i) => setEditTarget(i)}
              onDelete={(i) => setDeleteTarget(i)}
              onToggle={handleToggle}
            />
          ))
        )}
      </div>

      {/* ── Modal ── */}
      {editTarget !== null && (
        <TestiModal
          item={Object.keys(editTarget).length === 0 ? null : editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={handleSaved}
        />
      )}

      {/* ── Delete confirm ── */}
      {deleteTarget && (
        <ConfirmDialog
          item={deleteTarget}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          deleting={deleting}
        />
      )}

    </AdminLayout>
  );
}